// TypeScript file to provide strongly typed color variables

// Import the SCSS file to ensure the CSS variables are loaded
import './colors.scss';

// Function to ensure CSS variables are loaded
const ensureCssLoaded = () => {
  if (typeof window !== 'undefined' && document.readyState !== 'complete') {
    // If document is not fully loaded, force a CSS computation to ensure variables are applied
    const dummy = document.createElement('div');
    dummy.style.position = 'absolute';
    dummy.style.top = '-9999px';
    dummy.style.left = '-9999px';
    document.body.appendChild(dummy);
    getComputedStyle(dummy).position;
    document.body.removeChild(dummy);
  }
};

// Function to initialize tenant color explicitly
const initializeTenantColor = () => {
  if (typeof window !== 'undefined') {
    // Force CSS to be applied
    ensureCssLoaded();

    // Get the tenant color from CSS variable
    const tenantColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--tenant-color')
      .trim();

    // Apply it directly to document root as an inline style for immediate effect
    if (tenantColor) {
      document.documentElement.style.setProperty('--tenant-color', tenantColor);
      document.documentElement.style.setProperty('--purple', tenantColor);
    }
  }
};

// Define the colors object with getters that access CSS variables
const colors = {
  initializeTenantColor,
  get purple() {
    ensureCssLoaded();
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--purple')
      .trim();
  },
  get lighterPurple() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--lighter-purple')
      .trim();
  },
  get darkPurple() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--dark-purple')
      .trim();
  },
  get white() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--white')
      .trim();
  },
  get green() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--green')
      .trim();
  },
  get skyBlue() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--sky-blue')
      .trim();
  },
  get lightBlue() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--light-blue')
      .trim();
  },
  get blue() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--blue')
      .trim();
  },
  get darkBlack() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--dark-black')
      .trim();
  },
  get black() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--black')
      .trim();
  },
  get gray() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--gray')
      .trim();
  },
  get lightPurple() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--light-purple')
      .trim();
  },
  get softPurple() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--soft-purple')
      .trim();
  },
  get border() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--border')
      .trim();
  },
  get body() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--body')
      .trim();
  },
  get lightRose() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--light-rose')
      .trim();
  },
  get backgdrop() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--backgdrop')
      .trim();
  },
  get lightGray() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--light-gray')
      .trim();
  },
  get stormGray() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--storm-gray')
      .trim();
  },
  get darkGray() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--dark-gray')
      .trim();
  },
  get brownGray() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--brown-gray')
      .trim();
  },
  get warning() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--warning')
      .trim();
  },
  get rose() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--rose')
      .trim();
  },
  get approve() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--approve')
      .trim();
  },
  get background() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--background')
      .trim();
  },
  get stormWhite() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--storm-white')
      .trim();
  },
  get shadow() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--shadow')
      .trim();
  },
  get passed() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--passed')
      .trim();
  },
  get item() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--item')
      .trim();
  },
  get activeItem() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--active-item')
      .trim();
  },
  get index() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--index')
      .trim();
  },
  get faqDesc() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--faq-desc')
      .trim();
  },
  get row() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--row')
      .trim();
  },
  get actionPurple() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--action-purple')
      .trim();
  },
  get actionRose() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--action-rose')
      .trim();
  },
  get actionBlue() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--action-blue')
      .trim();
  },
  get actionGreen() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--action-green')
      .trim();
  },
  get roleBlue() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--role-blue')
      .trim();
  },
  get roleColor() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--role-color')
      .trim();
  },
  get lightSilver() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--light-silver')
      .trim();
  },
  get lightGray100() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--light-gray-100')
      .trim();
  },
  get borderGray() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--border-gray')
      .trim();
  },
  get textGray() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--text-gray')
      .trim();
  },
  get tenantColor() {
    ensureCssLoaded();
    // Get tenant color directly from CSS variable
    return (
      getComputedStyle(document.documentElement)
        .getPropertyValue('--tenant-color')
        .trim() || '#522583'
    ); // Default to purple if not set
  }
};

// For server-side rendering safety
const safeColors =
  typeof window !== 'undefined'
    ? colors
    : {
        initializeTenantColor: () => {}, // No-op function for server-side
        purple: '#3366FF',
        lighterPurple: 'rgba(51, 102, 255, 0.36)',
        darkPurple: '#2952cc',
        white: '#ffffff',
        green: '#258378',
        skyBlue: '#1976d2',
        lightBlue: '#33b1e8',
        blue: '#0077b5',
        darkBlack: '#000000',
        black: '#2a2a2a',
        gray: '#949494',
        lightPurple: 'rgba(51, 102, 255, 0.07)',
        softPurple: '#3366FF5c',
        border: '#efedf2',
        body: '#f6f5fa',
        lightRose: '#e1dbe7',
        backgdrop: '#00000028',
        lightGray: '#f1f5f6',
        stormGray: '#676768',
        darkGray: '#656565',
        brownGray: '#817575',
        warning: '#e45353',
        rose: '#f8aeae',
        approve: '#0fb398',
        background: '#efebf4',
        stormWhite: '#eae5f0',
        shadow: 'rgba(141, 156, 174, 0.25)',
        passed: '#0bb497',
        item: '#f9f9fd',
        activeItem: '#f5f1fc',
        index: '#9d96a8',
        faqDesc: '#5a5365',
        row: '#eef5f8',
        actionPurple: 'rgba(51, 102, 255, 0.41)',
        actionRose: '#f8aeae',
        actionBlue: '#bed7fe',
        actionGreen: '#a0e5d5',
        roleBlue: 'rgba(0, 158, 226, 0.1)',
        roleColor: '#009ee2',
        lightSilver: '#fdfdfd',
        lightGray100: '#b1b2c2',
        borderGray: '#bcbcbc',
        textGray: '#979797',
        tenantColor: '#3366FF' // Default tenant color
      };

export default safeColors;
